import type { PostHogConfig } from "posthog-js";

// authStore must load before loginState: its autorun reads loginState while the module loads
import "./js/stores/auth/authStore";
import loginState from "./js/stores/auth/loginState";
import uiStore from "./js/stores/uiStore";

import "./js/common/i18n";

import { IconCheckCircle } from "@tine/designsystem-icons/sharp";
import { PostHogErrorBoundary, PostHogProvider } from "posthog-js/react";
import { createRoot } from "react-dom/client";
import App from "./js/App";
import { APP_VERSION, IS_LOCAL_RUN, POSTHOG_API_KEY, POSTHOG_ENABLED } from "./js/common/environment";
import { stripQueryString } from "./js/common/errorReporting";
import { logger } from "./js/common/logger";
import Button from "./js/components/Button";
import ErrorAlertWithOptionalTracking from "./js/components/ErrorAlertWithOptionalTracking";
import { isBrowserMockRuntimeEnabled } from "./js/mocks/browserMockRuntime";
import theme from "./js/themes/theme";

const browserMockRuntimeEnabled = isBrowserMockRuntimeEnabled();

const UpgradeHappened = () => (
   <>
      <h2>
         <IconCheckCircle className="inline fill-success-600" /> En ny versjon av {theme.siteName} er tilgjengelig!
      </h2>
      <p>Trykk på knappen under for å laste inn den nye versjonen og fortsette handleturen.</p>
      <Button onClick={() => window.location.reload()}>Fortsett</Button>
   </>
);

const UnexpectedError = () => (
   <ErrorAlertWithOptionalTracking
      showCloseButton={false}
      fullWidth
      title="Det har oppstått en uforutsett feil!"
      gaErrorTracking={{ context: "site", text: "generic_site_error" }}
   >
      <p>Du kan prøve å trykke på knappen under for å starte om websiden.</p>
      <p>
         Feilen har blitt automatisk rapportert inn til {theme.storeCompanyName}, så vi kommer til å undersøke problemet nærmere
         for å se om vi kan unngå at dette skjer i fremtiden.
      </p>
      <Button variant="primary" onClick={() => window.location.reload()}>
         Start om siden
      </Button>
   </ErrorAlertWithOptionalTracking>
);

type SuggestedActionProps = {
   error: unknown;
};

const SuggestedAction = ({ error }: SuggestedActionProps) => (
   <div className="flex items-center justify-center m-12">
      <div className="w-full p-12 shadow bg-surface-default">
         {error instanceof Error && error.message.includes("Failed to fetch dynamically imported module") ? (
            <UpgradeHappened />
         ) : (
            <UnexpectedError />
         )}
      </div>
   </div>
);

const el = document.getElementById("root");
if (el) {
   const root = createRoot(el, {
      // React logs errors caught by an error boundary with console.error, which PostHog would report a second time
      onCaughtError: (error) => logger.warn("ui", "Error caught by error boundary", error)
   });
   const postHogOptions: Partial<PostHogConfig> = {
      api_host: "https://eu.i.posthog.com",
      defaults: "2025-11-30",
      capture_exceptions: {
         capture_unhandled_errors: true,
         capture_unhandled_rejections: true,
         capture_console_errors: true
      },
      // Clicks after which nothing changes on the page. Set here, since it overrides the project setting.
      capture_dead_clicks: true,
      // Impersonation links carry these in the page URL
      mask_personal_data_properties: true,
      custom_personal_data_properties: ["token", "bootstrapCode"],
      session_recording: {
         // Login sends the username and password in the query string. A custom mask function replaces PostHog's default
         // header and body scrubbing, so headers and bodies are never recorded, whatever the project settings say.
         recordHeaders: false,
         recordBody: false,
         maskCapturedNetworkRequestFn: (request) => ({ ...request, name: stripQueryString(request.name) })
      },
      // Local runs send nothing, but still load feature flags
      disable_session_recording: IS_LOCAL_RUN,
      before_send: (event) => {
         if (IS_LOCAL_RUN) {
            return null;
         }
         if (!event) {
            return event;
         }
         // Added per event rather than registered once, because the posthog.reset() at logout clears registered properties
         const properties = { ...event.properties, app_version: APP_VERSION };
         if (event.event === "$exception") {
            return {
               ...event,
               properties: { ...properties, login_state: loginState.currentState, data_version: uiStore.dataVersion }
            };
         }
         return { ...event, properties };
      }
   };

   const app = (
      <PostHogErrorBoundary fallback={SuggestedAction}>
         <App />
      </PostHogErrorBoundary>
   );

   root.render(
      POSTHOG_ENABLED && !browserMockRuntimeEnabled ? (
         <PostHogProvider apiKey={POSTHOG_API_KEY} options={postHogOptions}>
            {app}
         </PostHogProvider>
      ) : (
         app
      )
   );
}
